/**
 * Product Search Hook
 *
 * Full-text search using the unified `get_client_products` RPC.
 * Replaces the old `search_products_v2h` call.
 */

import { useQuery } from "@tanstack/react-query";
import type { UseProductSearchOptions, UseProductSearchResult } from "./types";
import { transformRawProducts } from "./transformations";
import { PRODUCT_CACHE_TIME } from "./queryKeys";
import { getProducts } from "../../services/products.service";

/**
 * Search products with full-text search
 */
export function useProductSearch(
  options: UseProductSearchOptions,
): UseProductSearchResult {
  const { query, limit = 50, filters, enabled = true } = options;

  const cacheKeyId = JSON.stringify({ query, limit, filters });
  const cacheKey = ["products", "search", cacheKeyId] as const;

  const searchQuery = useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      if (!query || query.trim().length === 0) {
        return { results: [], totalCount: 0 };
      }

      const result = await getProducts({
        search: query.trim(),
        limit,
        offset: 0,
        sort_by: "created_at",
        sort_order: "desc",
        category_id: filters?.categoryId,
        min_price: filters?.priceRange?.min,
        max_price: filters?.priceRange?.max,
        brand_id: filters?.brands?.[0]
          ? parseInt(filters.brands[0], 10)
          : undefined,
        product_type_id: filters?.productTypes?.[0]
          ? parseInt(filters.productTypes[0], 10)
          : undefined,
      });

      return {
        results: transformRawProducts(result.products as any[]),
        totalCount: result.totalCount,
      };
    },
    enabled: enabled && query.length > 0,
    staleTime: PRODUCT_CACHE_TIME.search,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    retryOnMount: false,
  });

  return {
    results: searchQuery.data?.results || [],
    isLoading: searchQuery.isLoading,
    isError: searchQuery.isError,
    error: searchQuery.error as Error | null,
    refetch: searchQuery.refetch as () => void,
    totalCount: searchQuery.data?.totalCount || 0,
  };
}

/**
 * Debounced search hook
 */
export function useDebouncedProductSearch(
  options: UseProductSearchOptions,
): UseProductSearchResult {
  return useProductSearch({
    ...options,
    enabled: (options.enabled ?? true) && (options.query?.length ?? 0) > 2,
  });
}
